/* SAP PDP Cadence Box controller.
 *
 * Wires the native cadence buy box rendered by snippets/sap-pdp-cadence-box.liquid.
 * Responsibilities:
 *   - Plan-mode radio toggle (update price display when sub is selected)
 *   - Sub-price display update when SHIP EVERY changes
 *   - Qty stepper (+ / - buttons)
 *   - Ajax submit to /cart/add.js + Aurora cart-drawer trigger
 *   - Error display on add failure
 *
 * Cart-drawer integration (native path — DO NOT replace with a manual innerHTML swap):
 *   Aurora's <cart-drawer id="CartDrawer"> renders its body from a
 *   <template data-sidebar-template="CartDrawer">, and each cart sub-block
 *   (items, subtotal, count, free-shipping bar) re-renders itself by listening
 *   to the drawer's own 'update-nodes' event — NOT by reading innerHTML. So we
 *   reuse Aurora's native refresh exactly the way a native add does:
 *     1. Request the cart-drawer section back with the add via the Section
 *        Rendering API (sections + sections_url params on /cart/add.js).
 *     2. Hand the rendered section HTML to drawer.purchaseHandler(html, json):
 *        it runs getTemplateContent() -> emit('update-nodes', {node}) -> open().
 *   A prior manual `cart-drawer.innerHTML` swap did NOT work (commit 085b4a8):
 *   the drawer re-clones from its <template> on open, overwriting the swap, and
 *   the web-component children never re-bind. purchaseHandler is the only
 *   correct entry point. (Refines memory reference_aurora-cart-drawer-update-nodes:
 *   update-nodes DOES reach the drawer's children when emitted on the
 *   <cart-drawer> element itself, which is what purchaseHandler does.)
 *
 * One-time vs subscription submit (radio-gated — revenue-critical):
 *   Sub-details (benefits + SHIP EVERY dropdown) are always visible in the DOM.
 *   The SHIP EVERY select ships disabled in Liquid (so a pre-JS native submit
 *   can't include a selling_plan) and is enabled here on init (Tula behavior).
 *   Submission is gated on the RADIO state — not the select's disabled property:
 *     One-time radio checked  -> selling_plan is never included in the POST body.
 *     Subscription radio checked -> selling_plan is read from the select and included.
 *   This prevents a one-time add from accidentally carrying a selling_plan value.
 *
 * Inline JS is forbidden in section files (SAP rule). All JS lives here.
 */
(function () {
  "use strict";

  function initCadenceBox(root) {
    if (!root) return;
    /* Idempotency: this box may be scanned more than once — on the homepage the
       snippet is rendered inside several quick-add modals and its <script> tag
       executes once per inclusion, so init() runs multiple times. Bind each box
       exactly once; a double bind would submit (add) twice. */
    if (root.dataset.sapCadenceWired === "1") return;
    root.dataset.sapCadenceWired = "1";

    var form = root.querySelector("[data-sap-cadence-form]");
    if (!form) return;

    /* When this box lives inside a quick-add <dialog> (homepage modal), close
       the dialog on a successful add so the cart drawer isn't behind its
       backdrop. null on the PDP (no enclosing dialog) — harmless. */
    var hostDialog = root.closest("dialog");

    var productId = root.dataset.productId;
    var radios = form.querySelectorAll("input[data-plan-mode]");
    var subRow = form.querySelector(".sap-cadence-box__row--sub");
    var freqSelect = form.querySelector("[data-selling-plan-select]");
    var qtyInput = form.querySelector("[data-qty-input]");
    var qtyBtns = form.querySelectorAll("[data-qty-step]");
    var submitBtn = form.querySelector("[data-sap-atc-btn]");
    var submitLabel = form.querySelector("[data-sap-atc-label]");
    var errorEl = form.querySelector("[data-sap-cadence-error]");
    var errorText = form.querySelector("[data-sap-error-text]");
    var subPriceEl = form.querySelector("[data-sub-display-price]");
    var atcPriceEl = form.querySelector("[data-sap-atc-price]");

    var ppPage = form.hasAttribute("data-pp-page");
    var titlePrice = ppPage ? document.querySelector("[data-pp-title-price]") : null;
    var titleStrike = ppPage ? document.querySelector("[data-pp-title-strike]") : null;
    var renewal = form.querySelector("[data-pp-renewal]");

    var onetimePrice = parseInt(form.dataset.onetimePrice, 10) || 0;
    var defaultSubPrice = parseInt(form.dataset.subPrice, 10) || 0;

    /* Intro offer (Full System promo): display-only first-order price written by
       Liquid as data-intro-price. In sub mode it wins over the plan allocation —
       the POSTed selling_plan is untouched; the real money change is the
       first-payment discount configured in Shopify/Recharge admin. */
    var introPrice = parseInt(form.dataset.introPrice, 10) || 0;
    var firstOrderOff = parseInt(form.dataset.firstOrderOffCents, 10) || 0;

    function selectedSubscriptionPrice() {
      var option = freqSelect && freqSelect.options[freqSelect.selectedIndex];
      return (option && parseInt(option.dataset.planAllocPrice, 10)) || defaultSubPrice || onetimePrice;
    }

    function firstOrderPrice() {
      return firstOrderOff ? Math.max(0, selectedSubscriptionPrice() - firstOrderOff) : introPrice;
    }

    function introActive() {
      return introPrice > 0;
    }

    /* ── Utility: money formatting (mirrors Liquid's money_without_trailing_zeros) ──
       B11 A: whole dollars, cents only when there are cents ($35, $29.75), the sticky bar's rule (sap-sticky-atc.js formatMoney). */
    function formatMoney(cents) {
      var dollars = (cents / 100).toFixed(2);
      if (dollars.slice(-3) === ".00") dollars = dollars.slice(0, -3);
      return "$" + dollars;
    }

    /* ── Radio toggle: update sub price display when switching modes ── */
    /* Sub-details are always visible (Tula behavior); no collapse toggling needed.
       applyMode only updates the price display when switching to sub mode. */
    function getActiveMode() {
      var checked = form.querySelector("input[data-plan-mode]:checked");
      return checked ? checked.dataset.planMode : "onetime";
    }

    /* ── ATC button price: unit price for the active mode × quantity ──
       Mirrors the mockup's "ADD TO BAG · $35.00". Updates on mode switch,
       SHIP-EVERY change, and qty stepping. */
    function getActiveUnitPrice() {
      if (getActiveMode() !== "sub") return onetimePrice;
      if (introActive()) return firstOrderPrice();
      if (freqSelect) {
        var opt = freqSelect.options[freqSelect.selectedIndex];
        var alloc = opt ? parseInt(opt.dataset.planAllocPrice, 10) : 0;
        if (alloc > 0) return alloc;
      }
      return defaultSubPrice || onetimePrice;
    }

    function readQuantity(value, writeBack) {
      var entered = Number(value === undefined ? (qtyInput ? qtyInput.value : 1) : value);
      var quantity = Number.isFinite(entered) ? Math.max(1, Math.floor(entered)) : 1;
      if (qtyInput && writeBack !== false) qtyInput.value = quantity;
      return quantity;
    }

    function updateAtcPrice(writeBack) {
      if (!atcPriceEl) return;
      var qty = readQuantity(undefined, writeBack);
      if (titlePrice) titlePrice.textContent = formatMoney(getActiveUnitPrice()).replace(/\.00$/, "");
      if (titleStrike) titleStrike.hidden = getActiveMode() !== "sub";
      if (renewal) renewal.hidden = getActiveMode() !== "sub";
      var firstOrderLabel = ppPage && document.querySelector("[data-pp-first-order]");
      if (firstOrderLabel) firstOrderLabel.hidden = getActiveMode() !== "sub";
      if (ppPage) updateProductCopy();
      atcPriceEl.textContent = "· " + formatMoney(getActiveUnitPrice() * qty);
    }

    function updateProductCopy() {
      var option = freqSelect && freqSelect.selectedOptions[0];
      var recurring = selectedSubscriptionPrice();
      var values = {
        first: formatMoney(firstOrderPrice()).replace(/\.00$/, ""),
        recurring: formatMoney(recurring).replace(/\.00$/, ""),
        price: formatMoney(recurring).replace(/\.00$/, ""),
        regular: formatMoney(onetimePrice).replace(/\.00$/, ""),
        percent: onetimePrice ? Math.round((onetimePrice - recurring) * 100 / onetimePrice) : 0,
        days: option && option.dataset.planDays || "45",
        schedules: freqSelect ? Array.from(freqSelect.options).map(function (o) { return o.dataset.planDays; }).filter(Boolean).sort(function (a,b) { return a-b; }).join(", ").replace(/, ([^,]+)$/, " or $1") : ""
      };
      form.querySelectorAll("[data-pp-copy]").forEach(function (node) {
        var template = form.getAttribute("data-pp-" + node.dataset.ppCopy);
        if (!template) return;
        node.textContent = template.replace(/\[(first|recurring|price|regular|percent|days|schedules)\]/g, function (_, key) { return values[key]; });
      });
      var badge = form.querySelector("[data-pp-saving-badge]");
      if (badge) badge.textContent = form.getAttribute("data-pp-saving-badge").replace("[percent]", values.percent);
    }

    function applyMode(mode) {
      if (ppPage && freqSelect) freqSelect.disabled = mode !== "sub";
      if (freqSelect && mode === "sub") {
        /* Ensure the price display reflects the currently selected plan */
        updateSubPrice();
      }
      updateAtcPrice();
    }

    radios.forEach(function (radio) {
      radio.addEventListener("change", function () {
        if (radio.checked) applyMode(radio.dataset.planMode);
      });
    });

    var howToggle = form.querySelector("[data-pp-how-toggle]");
    var howPanel = form.querySelector("[data-pp-how-panel]");
    if (howToggle && howPanel) {
      howToggle.addEventListener("click", function () {
        var open = howToggle.getAttribute("aria-expanded") !== "true";
        howToggle.setAttribute("aria-expanded", String(open));
        howPanel.hidden = !open;
      });
      form.addEventListener("keydown", function (event) {
        if (event.key !== "Escape" || howPanel.hidden) return;
        howPanel.hidden = true; howToggle.setAttribute("aria-expanded", "false"); howToggle.focus();
      });
    }

    /* Outside tap closes the details panel. This listens for click, not
       pointerdown: the panel sits above Add to Bag, so hiding it on pointerdown
       moved the button out from under the pointer before pointerup and the
       first tap on Add never became a click (audit F07, S140). On click the
       tap has already landed, so the add runs and then the panel closes. */
    document.addEventListener("click", function (event) {
      if (!howPanel || howPanel.hidden || howPanel.contains(event.target) || howToggle.contains(event.target)) return;
      howPanel.hidden = true; howToggle.setAttribute("aria-expanded", "false");
    });

    /* Run once on init so price display is correct for the default selection */
    applyMode(getActiveMode());

    /* Back from checkout can load this page from cached HTML: init paints the
       default plan, then the browser puts back the shopper's plan, Delivered
       every and quantity with no change event, so the button and sticky bar
       showed a price the add would not charge (U1 G2, call B13 A). On every
       pageshow, and once more a tick later in case the restore lands late,
       replay change on each control so every listener re-reads what is set. */
    function resyncControls() {
      [form.querySelector("input[data-plan-mode]:checked"), freqSelect, qtyInput].forEach(function (control) {
        if (control) control.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
    window.addEventListener("pageshow", function () {
      resyncControls();
      setTimeout(resyncControls, 0);
    });

    /* ── SHIP EVERY: update price display when frequency changes ── */
    function updateSubPrice() {
      if (!freqSelect || !subPriceEl) return;
      /* Intro offer: first order is the promo price on every cadence, so a
         frequency change must not overwrite it with the plan allocation. */
      if (introActive()) {
        subPriceEl.textContent = formatMoney(firstOrderPrice());
        if (ppPage) {
          var recurring = selectedSubscriptionPrice();
          var titleRegular = titleStrike && titleStrike.querySelector("s");
          var buyRegular = form.querySelector(".sap-cadence-box__price-was s");
          if (titleRegular) titleRegular.textContent = formatMoney(recurring).replace(/\.00$/, "");
          if (buyRegular) buyRegular.textContent = formatMoney(recurring);

        }
        return;
      }
      var selectedOpt = freqSelect.options[freqSelect.selectedIndex];
      if (!selectedOpt) return;
      var allocPrice = parseInt(selectedOpt.dataset.planAllocPrice, 10);
      if (allocPrice > 0) {
        subPriceEl.textContent = formatMoney(allocPrice);
      }
    }

    if (freqSelect) {
      /* Liquid ships the select disabled so a pre-JS native submit can't POST an
         unconsented selling_plan; from here on doAdd() gates it on the radio. */
      freqSelect.disabled = ppPage ? getActiveMode() !== "sub" : false;
      freqSelect.addEventListener("change", function () {
        updateSubPrice();
        updateAtcPrice();
      });
    }

    /* ── QTY stepper ── */
    qtyBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        if (!qtyInput) return;
        var step = parseInt(btn.dataset.qtyStep, 10);
        readQuantity(readQuantity() + step);
        updateAtcPrice();
      });
    });

    if (qtyInput) {
      qtyInput.addEventListener("input", function () { updateAtcPrice(false); });
      qtyInput.addEventListener("change", updateAtcPrice);
    }

    /* ── Ajax form submit ── */
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      doAdd();
    });

    function showError(msg) {
      if (errorText) errorText.textContent = msg || "Could not add to bag.";
      if (errorEl) errorEl.hidden = false;
    }

    function clearError() {
      if (errorEl) errorEl.hidden = true;
      if (errorText) errorText.textContent = "";
    }

    /* Sold-out: Liquid renders the button disabled with data-sold-out — never
       re-enable it or overwrite its label, and never POST for it. */
    var soldOut = submitBtn && submitBtn.hasAttribute("data-sold-out");

    function setLoading(loading) {
      if (soldOut) return;
      /* aria-disabled, not disabled: a disabled button drops keyboard focus to the
         page top, so focus stays on Add to Bag while adding and after an error
         (A11y A5). The adding lock below still blocks a second add. */
      function busy(btn) { if (loading) btn.setAttribute("aria-disabled", "true"); else btn.removeAttribute("aria-disabled"); }
      if (submitBtn) busy(submitBtn);
      if (submitLabel) submitLabel.textContent = loading ? "Adding..." : "Add to Bag";
      /* The sticky ATC bar delegates to this form via requestSubmit(); mirror the
         loading state onto its button so a shopper can't re-tap it mid-add. */
      document.querySelectorAll(".sap-sticky-atc__btn").forEach(busy);
    }

    /* In-flight lock. Disabling submitBtn is NOT enough: form.requestSubmit()
       (the sticky bar's path) performs form submission without consulting the
       submit button's disabled state, so a double-tap fires doAdd() twice and
       double-adds to the cart. */
    var adding = false;

    function doAdd() {
      if (adding || soldOut) return;
      adding = true;
      var applied = false;
      var reloading = false;
      clearError();
      setLoading(true);
      updateAtcPrice();

      var fd = new FormData(form);

      /* Radio-gated selling_plan: check which radio is checked BEFORE reading FormData.
         The SHIP EVERY select is always enabled (Tula behavior), so FormData will always
         contain a selling_plan value. We must explicitly check the radio state and only
         include selling_plan when the subscription radio is checked.
         One-time checked -> selling_plan never sent -> clean one-time add.
         Subscription checked -> selling_plan from select sent -> subscription add. */
      var subRadio = form.querySelector('input[data-plan-mode="sub"]');
      var isSubMode = subRadio && subRadio.checked;

      /* Locate Aurora's cart drawer + its REAL section id (read at runtime from
         the live element, never hard-coded) so we can request the rendered
         cart-drawer section back in the add response and feed it to the
         drawer's own purchaseHandler. */
      var drawer = document.querySelector("cart-drawer");
      var sectionId = (drawer && drawer.dataset.sectionId) || "cart-drawer";
      var wasInert = drawer && drawer.inert;
      function reloadCart() {
        reloading = true;
        if (drawer) drawer.inert = true;
        var cart = new URL((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || "/") + "cart", window.location.origin);
        var current = new URL(window.location.href);
        if (current.searchParams.has("preview_theme_id")) cart.searchParams.set("preview_theme_id", current.searchParams.get("preview_theme_id"));
        window.location.assign(cart.href);
      }

      /* Build the add request exactly the way Aurora's native product form does
         (getQueryConfig in main-*.js): a FormData body with
         Accept: application/javascript + X-Requested-With and NO explicit
         Content-Type (the browser sets the multipart boundary). This matters:
         a urlencoded body does NOT trigger Section Rendering — /cart/add.js
         returns 200 with the item but no `sections` object, so the drawer can't
         refresh. We build FormData manually (not from the <form>) so the
         always-enabled SHIP EVERY select can't leak a selling_plan into a
         one-time add. `sections` is the cart-drawer's own section id. */
      var body = new FormData();
      body.append("id", fd.get("id"));
      /* Clamp to 1: a typed "0" passes `|| "1"` (non-empty string) and
         /cart/add.js answers a qty-0 add with 200 + {} — nothing added while
         the UI resolves as success. Steppers can't reach 0; only typing can. */
      body.append("quantity", String(readQuantity()));
      if (isSubMode && freqSelect && freqSelect.value) {
        body.append("selling_plan", freqSelect.value);
      }
      body.append("sections", sectionId);
      body.append("sections_url", window.location.pathname);

      fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: body,
      })
        .then(function (res) {
          applied = res.ok;
          return res.text().then(function (text) {
            var json = null;
            try {
              json = JSON.parse(text);
            } catch (_) {}
            if (!res.ok) {
              throw new Error(
                (json && (json.description || json.message)) || "Could not add to bag. Please try again.",
              );
            }
            return json;
          });
        })
        .then(async function (json) {
          /* Native refresh: hand the freshly-rendered cart-drawer section to
             Aurora's own purchaseHandler. It extracts the <template> content,
             emits 'update-nodes' so every cart sub-block (items, subtotal,
             count, free-shipping bar) re-renders, then opens the drawer — the
             exact path a native add takes. This is why a manual innerHTML swap
             failed: the drawer re-clones from its <template> on open. */
          var sectionHTML = json && json.sections && json.sections[sectionId];
          if (!sectionHTML) {
            if (drawer) drawer.inert = true;
            var address = new URL(window.location.href);
            address.hash = "";
            address.searchParams.delete("section_id");
            address.searchParams.set("sections", sectionId);
            var response = await fetch(address.href, { credentials: "same-origin", cache: "no-store" });
            if (!response.ok) throw new Error("Cart section unavailable");
            var sections = await response.json();
            sectionHTML = sections && sections[sectionId];
            json = Object.assign({}, json, { sections: sections });
          }
          /* Close the host modal (if any) before opening the cart drawer. */
          if (hostDialog && hostDialog.open) hostDialog.close();
          if (drawer && typeof drawer.purchaseHandler === "function" && sectionHTML) {
            /* purchaseHandler expects a PARSED Document (it runs querySelector on
               the arg via getTemplateContent), not a raw HTML string — Aurora
               calls parseHTML() first. Mirror that with DOMParser. */
            var parsedDrawer = new DOMParser().parseFromString(sectionHTML, "text/html");
            if (!parsedDrawer.querySelector('[data-sidebar-template="' + drawer.id + '"]')) throw new Error("Cart section unavailable");
            drawer.inert = wasInert;
            await drawer.purchaseHandler(parsedDrawer, json);
            document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
            return;
          }
          reloadCart();
        })
        .catch(function (err) {
          if (applied) {
            reloadCart();
            return;
          }
          showError(err.message || "Could not add to bag.");
          /* The sticky ATC bar delegates adds here while the buy box (and its
             error element) is scrolled off-screen — surface the failure there
             too, or the sticky button just reverts with zero feedback. */
          document.dispatchEvent(
            new CustomEvent("sap:add-error", {
              detail: { message: err.message || "Could not add to bag." },
            }),
          );
        })
        .finally(function () {
          if (!reloading) {
            adding = false;
            setLoading(false);
          }
        });
    }
  }

  /* ── Bootstrap: init all cadence boxes on page load ── */
  function init() {
    document.querySelectorAll(".sap-cadence-box").forEach(initCadenceBox);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* Re-wire on Shopify theme-editor section re-renders */
  document.addEventListener("shopify:section:load", function (e) {
    if (e.target) {
      e.target.querySelectorAll(".sap-cadence-box").forEach(initCadenceBox);
    }
  });
})();
